/**
 * Subscription domain types and pure helpers.
 *
 * This module is framework-free: no React, no Supabase, no Capacitor.
 * Anything here can be unit-tested directly and reused from hooks,
 * Edge Function client code, or scripts without dragging in IO.
 */

export type SubscriptionTier = 'free' | 'premium' | 'trialing' | 'lifetime';
export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'unpaid' | 'trialing' | null;
export type BillingTier = 'weekly' | 'monthly' | 'annual';
export type MembershipOfferKind =
  | 'standard'
  | 'annual_trial'
  | 'monthly_trial'
  | 'onboarding_yearly';

export interface SubscriptionInfo {
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  stripeCustomerId: string | null;
  billingTier: BillingTier | null;
  scheduledBillingTier: BillingTier | null;
  scheduledBillingChangeAt: string | null;
  provider: 'stripe' | 'paypal' | 'native_store' | 'none';
  managementUrl: string | null;
  store: string | null;
  productIdentifier: string | null;
  /** RevenueCat periodType when native billing supplied it. INTRO and TRIAL are trials. */
  periodType?: string | null;
}

export interface Invoice {
  id: string;
  date: string;
  amount: number;
  currency: string;
  status: string;
  invoicePdf: string | null;
}

/** Row shape of user_subscriptions_v2 as consumed by the client. */
export interface SubscriptionRecord {
  billing_provider: string | null;
  status: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean | null;
  stripe_customer_id: string | null;
  paypal_subscription_id: string | null;
  paypal_manage_url: string | null;
  subscription_type: string;
  scheduled_subscription_type: string | null;
  scheduled_change_at: string | null;
  /** premium_legacy, vip or pro. Optional: older fixtures and callers omit it. */
  plan_tier?: string | null;
}

/**
 * Check if user has premium access
 */
export function hasPremiumAccess(tier: SubscriptionTier): boolean {
  return tier === 'premium' || tier === 'trialing' || tier === 'lifetime';
}

export function normalizeBillingTier(value: string | null | undefined): BillingTier | null {
  if (!value) return null;
  const normalized = value.toLowerCase();
  if (normalized === 'weekly' || normalized === 'week') return 'weekly';
  if (normalized === 'monthly' || normalized === 'month') return 'monthly';
  if (normalized === 'annual' || normalized === 'yearly' || normalized === 'year') return 'annual';
  return null;
}

export function normalizeSubscriptionStatus(value: string | null | undefined): SubscriptionStatus {
  if (
    value === 'active' ||
    value === 'past_due' ||
    value === 'canceled' ||
    value === 'unpaid' ||
    value === 'trialing'
  ) {
    return value;
  }
  return null;
}

export function normalizeSubscriptionTier(value: string | null | undefined): SubscriptionTier | null {
  if (
    value === 'free' ||
    value === 'premium' ||
    value === 'trialing' ||
    value === 'lifetime'
  ) {
    return value;
  }
  return null;
}

export function hasFutureIsoDate(value: string | null | undefined, now: number = Date.now()): boolean {
  if (!value) return false;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && timestamp > now;
}
